// src/pages/Product/Components/ProductHero.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addItem } from '../../../redux/features/Cart/cartSlice';
import { fetchProductDetails } from '../../../redux/features/Product/productThunk';
import { getUserReview, createReview, deleteReview } from '../../../redux/features/Product/reviewThunk';
import { FaStar, FaTelegram, FaWhatsapp, FaInstagram, FaShoppingBag, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';
import { MdOutlinePriceChange } from 'react-icons/md';
import { GiWoodBeam } from 'react-icons/gi';
import StarRating from '../../../components/ui/StarRating';

function ProductHero({ product }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { userInfo } = useSelector((state) => state.auth) || {};
  const { userReview, loading: reviewLoading } = useSelector((state) => state.reviews);

  const [count, setCount] = useState(1);
  const [selectedColors, setSelectedColors] = useState([]);
  const [colorErrors, setColorErrors] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [toast, setToast] = useState(null);

  const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
  const isOutOfStock = product.countInStock <= 0;

  // ✅ خرید عمده فقط وقتی فعال است که هم در ادمین فعال شده باشد و هم قیمت/حداقل تعداد معتبر داشته باشد
  const wholesaleEnabled =
    product.wholesale_enabled === true &&
    Number(product.wholesale_price) > 0 &&
    Number(product.wholesale_min_quantity) > 1;

  // آرایه‌ی تصاویر - فقط ۴ تصویر اول
  const allImages = [product.image, ...(product.gallery || [])].filter(Boolean);
  const displayImages = allImages.slice(0, 4);

  const colorStockMap = {};
  product.color_stocks?.forEach((cs) => {
    colorStockMap[cs.color] = cs.stock;
  });

  // دریافت امتیاز کاربر
  useEffect(() => {
    if (userInfo?.access && product._id) {
      dispatch(getUserReview(product._id));
    }
  }, [dispatch, product._id, userInfo]);

  useEffect(() => {
    setSelectedColors((prev) => {
      const newLength = count;
      if (prev.length < newLength) {
        return [...prev, ...Array(newLength - prev.length).fill(null)];
      } else if (prev.length > newLength) {
        return prev.slice(0, newLength);
      }
      return prev;
    });
    setColorErrors(Array(count).fill(''));
  }, [count]);

  // ✅ اگر خرید عمده غیرفعال شد، حالت bulk هم غیرفعال شود
  useEffect(() => {
    if (!wholesaleEnabled && isBulk) {
      setIsBulk(false);
    }
  }, [wholesaleEnabled, isBulk]);

  // ===== توابع تغییر تصویر =====
  const handleImageChange = (index) => {
    if (index === activeImageIndex || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveImageIndex(index);
      setTimeout(() => setIsTransitioning(false), 300);
    }, 200);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 400);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveImageIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 400);
  };

  // ✅ قیمت نهایی بر اساس عادی/عمده
  const getPrice = () => {
    let price = Number(product.price) || 0;

    // تخفیف عمومی
    if (product.discountPercentage && product.discountPercentage > 0) {
      price = price * (1 - product.discountPercentage / 100);
    }

    // تخفیف خرید عمده
    if (isBulk && wholesaleEnabled) {
      price = Number(product.wholesale_price);
    }

    return Math.round(price);
  };

  const increaseCount = () => {
    const maxStock = product.countInStock || 10;
    if (count < maxStock) setCount((prev) => prev + 1);
  };

  const decreaseCount = () => {
    if (count > 1) setCount((prev) => prev - 1);
  };

  const formatDimensions = (dim) => {
    if (!dim) return '---';
    if (typeof dim === 'object') {
      const { width, depth, height } = dim;
      return `${width} × ${depth} × ${height} سانتی‌متر`;
    }
    return dim;
  };

  // ===== کپی آیدی تلگرام =====
  const copyTelegramId = () => {
    const telegramId = '@Erisswood';

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(telegramId)
        .then(() => {
          setToast({ message: 'آیدی تلگرام اریس گالری کپی شد ✅', type: 'success' });
          setTimeout(() => setToast(null), 3000);
        })
        .catch(() => copyFallback(telegramId));
    } else {
      copyFallback(telegramId);
    }
  };

  const copyFallback = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setToast({ message: 'آیدی تلگرام اریس گالری کپی شد ✅', type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast({ message: 'خطا در کپی کردن ❌', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // ===== اشتراک‌گذاری =====
  const shareProduct = (platform) => {
    const url = window.location.href;
    const text = `${product.name} - قیمت: ${getPrice().toLocaleString()} تومان`;
    const shareLinks = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    };
    window.open(shareLinks[platform], '_blank', 'width=600,height=500');
  };

  const handleSelectColor = (index, colorIndex) => {
    const newSelected = [...selectedColors];
    newSelected[index] = colorIndex;
    setSelectedColors(newSelected);
    const newErrors = [...colorErrors];
    newErrors[index] = '';
    setColorErrors(newErrors);
  };

  // ===== مدیریت امتیاز =====
  const handleRate = (value) => {
    if (!userInfo?.access) {
      navigate('/phone?redirect=' + window.location.pathname);
      return;
    }
    setRatingError('');
    dispatch(createReview({ productId: product._id, rating: value }))
      .unwrap()
      .then(() => {
        dispatch(fetchProductDetails(product._id));
      })
      .catch((err) => setRatingError(err?.detail || 'خطا در ثبت امتیاز'));
  };

  const handleDeleteRating = () => {
    if (window.confirm('آیا از حذف امتیاز خود مطمئن هستید؟')) {
      dispatch(deleteReview(product._id))
        .unwrap()
        .then(() => {
          dispatch(fetchProductDetails(product._id));
        })
        .catch((err) => setRatingError(err?.detail || 'خطا در حذف امتیاز'));
    }
  };

  // ===== افزودن به سبد خرید =====
  const addToCart = () => {
    if (isOutOfStock) {
      alert('این محصول در حال حاضر موجود نیست.');
      return;
    }

    // ✅ اعتبارسنجی خرید عمده
    if (isBulk && wholesaleEnabled) {
      const minQty = Number(product.wholesale_min_quantity) || 0;
      if (count < minQty) {
        alert(
          `حداقل تعداد برای خرید عمده ${minQty} عدد است.\nتعداد فعلی شما: ${count} عدد.`
        );
        return;
      }
    }

    if (hasColors) {
      const missingIndices = selectedColors
        .map((val, idx) => (val === null || val === undefined ? idx : -1))
        .filter((idx) => idx !== -1);

      if (missingIndices.length > 0) {
        const newErrors = [...colorErrors];
        missingIndices.forEach((idx) => {
          newErrors[idx] = `لطفاً رنگ برای محصول ${idx + 1} را انتخاب کنید`;
        });
        setColorErrors(newErrors);
        return;
      }

      const selectedCounts = {};
      selectedColors.forEach((colorIndex) => {
        if (colorIndex !== null && colorIndex !== undefined) {
          const colorId = product.colors[colorIndex]?.id;
          if (colorId) {
            selectedCounts[colorId] = (selectedCounts[colorId] || 0) + 1;
          }
        }
      });

      const errors = [];
      for (const [colorId, qty] of Object.entries(selectedCounts)) {
        const stock = colorStockMap[Number(colorId)] || 0;
        if (qty > stock) {
          const colorName = product.colors.find((c) => c.id === Number(colorId))?.name || 'نامشخص';
          errors.push(`موجودی رنگ "${colorName}" (${stock} عدد) کافی نیست.`);
        }
      }
      if (errors.length > 0) {
        alert(errors.join('\n'));
        return;
      }
    }

    const finalPrice = getPrice();

    if (hasColors) {
      selectedColors.forEach((colorIndex) => {
        const selectedColorObj = product.colors[colorIndex];
        const colorId = selectedColorObj ? selectedColorObj.id : null;
        dispatch(
          addItem({
            product: product._id,
            name: product.name,
            image: product.image,
            price: finalPrice,
            countInStock: product.countInStock,
            qty: 1,
            selectedColor: colorId,
            selectedColorName: selectedColorObj ? selectedColorObj.name : null,
          })
        );
      });
    } else {
      dispatch(
        addItem({
          product: product._id,
          name: product.name,
          image: product.image,
          price: finalPrice,
          countInStock: product.countInStock,
          qty: count,
          selectedColor: null,
        })
      );
    }

    setColorErrors(Array(count).fill(''));
    navigate('/cart');
  };

  const ToastMessage = () => {
    if (!toast) return null;
    return (
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-fade-in-down">
        <div className="flex items-center gap-3 px-6 py-3.5 rounded-2xl shadow-2xl bg-white border-2 border-[#7a9e6f]">
          <span className="text-xl">✅</span>
          <span className="text-sm font-medium text-[#2d2d2d]">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="mr-2 text-gray-400 hover:text-[#7a9e6f] transition-colors duration-200 text-lg font-light"
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6" dir="rtl">
      <ToastMessage />

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
        {/* ===== بخش تصاویر ===== */}
        <article className="w-full lg:w-[33%] xl:w-[25%] flex justify-center">
          <div className="sticky top-4 bg-white rounded-2xl shadow-lg overflow-hidden w-full max-w-[400px] lg:max-w-full">
            <div className="relative w-full aspect-square bg-[#F4F1EA] overflow-hidden">
              <img
                src={displayImages[activeImageIndex] || '/placeholder-image.jpg'}
                alt={product.name}
                className={`w-full h-full object-cover object-center transition-all duration-500 ease-in-out ${
                  isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                }`}
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
              />

              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    disabled={isTransitioning}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 hover:text-[#9EAD8C] w-9 h-9 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                    aria-label="تصویر قبلی"
                  >
                    <FaChevronRight size={16} />
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={isTransitioning}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 hover:text-[#9EAD8C] w-9 h-9 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                    aria-label="تصویر بعدی"
                  >
                    <FaChevronLeft size={16} />
                  </button>

                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    {activeImageIndex + 1} / {displayImages.length}
                  </div>
                </>
              )}

              {isTransitioning && (
                <div className="absolute inset-0 bg-[#F4F1EA]/80 flex items-center justify-center">
                  <div className="w-10 h-10 border-4 border-[#9EAD8C] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 p-2 bg-white border-t border-gray-100">
              {[...Array(3)].map((_, index) => {
                const img = displayImages[index + 1] || null;
                const isActive = activeImageIndex === index + 1;
                const hasImage = img !== null;

                return (
                  <button
                    key={index}
                    onClick={() => hasImage && handleImageChange(index + 1)}
                    className={`
                      relative aspect-square overflow-hidden rounded-lg
                      transition-all duration-300 ease-in-out
                      focus:outline-none
                      ${!hasImage ? 'cursor-default' : 'cursor-pointer'}
                      ${
                        isActive && hasImage
                          ? 'ring-2 ring-[#9EAD8C] ring-offset-2 shadow-lg scale-[0.98]'
                          : hasImage
                          ? 'hover:scale-95 hover:shadow-md hover:ring-1 hover:ring-[#9EAD8C]/50'
                          : ''
                      }
                    `}
                    disabled={!hasImage}
                  >
                    {hasImage ? (
                      <>
                        <img
                          src={img}
                          alt={`${product.name} - تصویر ${index + 2}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />

                        {isActive && (
                          <div className="absolute inset-0 bg-[#9EAD8C]/15 flex items-center justify-center">
                            <div className="w-7 h-7 rounded-full bg-[#9EAD8C] text-white flex items-center justify-center text-xs font-bold shadow-lg">
                              ✓
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                        +
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </article>

        {/* ===== بخش اطلاعات محصول ===== */}
        <article className="w-full lg:w-[67%] xl:w-[70%]">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
            <div className="p-4 sm:p-5 lg:p-6">
              <div className="flex flex-col lg:flex-row lg:gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-1">
                    <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 leading-relaxed flex-1">
                      {product.name}
                    </h1>
                    <GiWoodBeam className="flex-shrink-0 mt-1" color="#9EAD8C" size={24} />
                  </div>
                  <div className="border-b border-gray-200 my-3" />
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <FaStar
                          key={index}
                          size={15}
                          className={index < (product.rating || 0) ? 'text-yellow-400' : 'text-gray-200'}
                        />
                      ))}
                      <span className="text-xs text-gray-500 mr-1">({product.rating || 0})</span>
                    </div>
                    <span
                      className={`text-xs font-medium px-3 py-1 rounded-full ${
                        isOutOfStock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {isOutOfStock ? 'ناموجود' : 'موجود'}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-gray-400 font-medium">توضیحات کوتاه</span>
                    <p className="text-sm text-gray-700 leading-relaxed mt-1 line-clamp-3">
                      {product.description}
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">امتیاز شما:</span>
                        {userInfo?.access ? (
                          <>
                            <StarRating
                              rating={userReview?.rating || 0}
                              onRate={handleRate}
                              disabled={reviewLoading}
                              size={22}
                            />
                            {userReview?.rating && (
                              <button
                                onClick={handleDeleteRating}
                                className="text-xs text-red-500 hover:text-red-700 transition-colors"
                                disabled={reviewLoading}
                              >
                                حذف امتیاز
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">برای امتیازدهی وارد شوید</span>
                        )}
                      </div>
                      {ratingError && <span className="text-xs text-red-500">{ratingError}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-gray-600">
                        میانگین: <span className="font-bold">{product.rating || 0}</span>
                      </span>
                      <span className="text-sm text-gray-600">
                        ({product.numReviews || 0} نظر)
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-1 gap-2 mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">شناسه محصول :</span>
                      <span className="text-sm text-primary font-semibold">
                        {product.shareCode || product._id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">آخرین بروزرسانی :</span>
                      <span className="text-sm text-primary font-semibold">
                        {product.publishDate || new Date(product.createdAt).toLocaleDateString('fa-IR')}
                      </span>
                    </div>
                  </div>

                  {/* اشتراک‌گذاری */}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400 font-medium">اشتراک گذاری :</span>
                    <div className="flex gap-2">
                      <button
                        onClick={copyTelegramId}
                        className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded-full transition-all duration-300 hover:scale-110"
                        aria-label="کپی آیدی تلگرام"
                      >
                        <FaTelegram className="text-[#26A5E4] text-lg" />
                      </button>

                      <button
                        onClick={() => shareProduct('whatsapp')}
                        className="p-1.5 bg-green-50 hover:bg-green-100 rounded-full transition-all duration-300 hover:scale-110"
                        aria-label="اشتراک در واتساپ"
                      >
                        <FaWhatsapp className="text-[#25D366] text-lg" />
                      </button>

                      <a
                        href="https://www.instagram.com/eriss_wood?stkn=Ymt6ODE0MzY1NWE2"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 bg-pink-50 hover:bg-pink-100 rounded-full transition-all duration-300 hover:scale-110 inline-flex"
                        aria-label="پیج اینستاگرام"
                      >
                        <FaInstagram className="text-[#E4405F] text-lg" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block lg:w-[340px] xl:w-[380px] flex-shrink-0 mt-4 lg:mt-0">
                  <div className="sticky top-4">
                    <PurchasePanel
                      product={product}
                      count={count}
                      selectedColors={selectedColors}
                      colorErrors={colorErrors}
                      colorStockMap={colorStockMap}
                      isBulk={isBulk}
                      setIsBulk={setIsBulk}
                      getPrice={getPrice}
                      addToCart={addToCart}
                      increaseCount={increaseCount}
                      decreaseCount={decreaseCount}
                      handleSelectColor={handleSelectColor}
                      hasColors={hasColors}
                      isOutOfStock={isOutOfStock}
                      wholesaleEnabled={wholesaleEnabled}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:hidden p-4 sm:p-5 border-t border-gray-200">
              <PurchasePanel
                product={product}
                count={count}
                selectedColors={selectedColors}
                colorErrors={colorErrors}
                colorStockMap={colorStockMap}
                isBulk={isBulk}
                setIsBulk={setIsBulk}
                getPrice={getPrice}
                addToCart={addToCart}
                increaseCount={increaseCount}
                decreaseCount={decreaseCount}
                handleSelectColor={handleSelectColor}
                hasColors={hasColors}
                isOutOfStock={isOutOfStock}
                wholesaleEnabled={wholesaleEnabled}
              />
            </div>
          </div>
        </article>
      </div>

      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -30px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
}

const PurchasePanel = ({
  product,
  count,
  selectedColors,
  colorErrors,
  colorStockMap,
  isBulk,
  setIsBulk,
  getPrice,
  addToCart,
  increaseCount,
  decreaseCount,
  handleSelectColor,
  hasColors,
  isOutOfStock,
  wholesaleEnabled,
}) => {
  const formatDimensions = (dim) => {
    if (!dim) return '---';
    if (typeof dim === 'object') {
      const { width, depth, height } = dim;
      return `${width} × ${depth} × ${height} سانتی‌متر`;
    }
    return dim;
  };

  const getRemainingStock = (rowIndex, colorId) => {
    let selectedSoFar = 0;
    for (let i = 0; i < rowIndex; i++) {
      const selectedIndex = selectedColors[i];
      if (selectedIndex !== null && selectedIndex !== undefined) {
        const colorObj = product.colors[selectedIndex];
        if (colorObj && colorObj.id === colorId) {
          selectedSoFar++;
        }
      }
    }
    const originalStock = colorStockMap[colorId] || 0;
    return originalStock - selectedSoFar;
  };

  const minWholesaleQty = Number(product.wholesale_min_quantity) || 0;

  return (
    <div className="p-3 bg-gray-50/50 sm:p-5 rounded-xl border border-gray-200 h-full sm:-mt-6">
      <div className="flex items-center gap-2">
        <p className="text-sm text-[#9EAD8C] font-medium">خرید اینترنتی از فروشگاه</p>
      </div>
      <div className="border-b border-gray-200 mb-3" />

      <div className="grid grid-cols-1 gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 whitespace-nowrap">ابعاد :</span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {formatDimensions(product.dimensions)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 whitespace-nowrap">زمان آماده‌سازی :</span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {product.prepTime || '---'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 whitespace-nowrap">گارانتی :</span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {product.guarantee ? `${product.guarantee} ماه` : '---'}
          </span>
        </div>
      </div>

      {hasColors && !isOutOfStock && (
        <div className="mb-2">
          <span className="text-xs text-gray-400 font-medium block mb-2">انتخاب رنگ برای هر محصول:</span>
          {Array.from({ length: count }).map((_, idx) => {
            const selectedColorIndex = selectedColors[idx];
            const selectedColorObj =
              selectedColorIndex !== null && selectedColorIndex !== undefined
                ? product.colors[selectedColorIndex]
                : null;
            const selectedColorId = selectedColorObj?.id;

            const colorRemaining = {};
            product.colors.forEach((color) => {
              colorRemaining[color.id] = getRemainingStock(idx, color.id);
            });

            return (
              <div key={idx} className="mb-3 p-2 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-600">محصول {idx + 1}:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {product.colors.map((color, colorIndex) => {
                      const remaining = colorRemaining[color.id] || 0;
                      const isOutOfStockColor = remaining <= 0;
                      return (
                        <button
                          key={color.id || colorIndex}
                          type="button"
                          onClick={() => !isOutOfStockColor && handleSelectColor(idx, colorIndex)}
                          className={`w-8 h-8 rounded-full border-2 transition-all relative ${
                            selectedColors[idx] === colorIndex
                              ? 'border-black scale-110 shadow-md'
                              : 'border-gray-300'
                          } ${isOutOfStockColor ? 'opacity-30 cursor-not-allowed' : ''}`}
                          style={{ backgroundColor: color.code }}
                          title={`${color.name} (${remaining} عدد باقی‌مانده)`}
                          disabled={isOutOfStockColor}
                        />
                      );
                    })}
                  </div>
                </div>
                {selectedColorObj && (
                  <div>
                    <span className="text-xs text-gray-600">
                      رنگ انتخاب‌شده: {selectedColorObj.name}
                    </span>
                    {(() => {
                      const remainingAfter = getRemainingStock(idx, selectedColorId) - 1;
                      const isLowStock = remainingAfter === 1;
                      const isOut = remainingAfter <= 0;
                      return (
                        <span className={`text-xs block ${isLowStock ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                          موجودی باقی‌مانده: {remainingAfter} عدد
                          {isLowStock && ' (تنها یک عدد باقی مانده!)'}
                          {isOut && ' (موجودی تمام شد!)'}
                        </span>
                      );
                    })()}
                  </div>
                )}
                {colorErrors[idx] && (
                  <span className="text-xs text-red-500 block mt-1">{colorErrors[idx]}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between bg-white p-3 rounded-xl shadow-sm border border-gray-100 mb-3">
        <div className="flex items-center gap-2">
          <MdOutlinePriceChange className="text-[#9EAD8C] text-xl" />
          <span className="text-xs text-gray-400">قیمت واحد :</span>
        </div>
        <div className="text-left">
          <span className="text-xl font-bold text-[#9EAD8C]">
            {getPrice().toLocaleString('fa-IR')}
          </span>
          <span className="text-sm text-gray-400 mr-1">تومان</span>
        </div>
      </div>

      {/* ✅ چک‌باکس خرید عمده — فقط وقتی در ادمین فعال شده باشد */}
      {!isOutOfStock && wholesaleEnabled && (
        <div className="mb-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="bulkPurchase"
              className="w-4 h-4 accent-[#8A9A7B] cursor-pointer"
              checked={isBulk}
              onChange={() => setIsBulk(!isBulk)}
            />
            <label htmlFor="bulkPurchase" className="text-sm text-gray-700 cursor-pointer select-none font-medium">
              خرید به صورت عمده
            </label>
          </div>

          <div className="mt-2 text-xs text-gray-500 leading-5">
            <div>
              <span className="text-gray-400">قیمت عمده: </span>
              <span className="font-bold text-[#8A9A7B]">
                {Number(product.wholesale_price).toLocaleString('fa-IR')} تومان
              </span>
            </div>
            <div>
              <span className="text-gray-400">حداقل تعداد: </span>
              <span className="font-bold text-[#8A9A7B]">{minWholesaleQty} عدد</span>
            </div>
            {isBulk && count < minWholesaleQty && (
              <div className="mt-1 text-red-500 font-medium">
                ⚠️ حداقل {minWholesaleQty} عدد برای خرید عمده لازم است.
              </div>
            )}
          </div>
        </div>
      )}

      {!isOutOfStock && (
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs text-gray-400 font-medium whitespace-nowrap">تعداد:</span>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
            <button
              className="w-10 h-10 bg-gray-50 hover:bg-gray-100 text-gray-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50"
              onClick={decreaseCount}
              disabled={count <= 1}
            >
              <AiOutlineMinus size={18} />
            </button>
            <span className="w-14 text-center font-bold text-gray-800 text-lg">{count}</span>
            <button
              className="w-10 h-10 bg-gray-50 hover:bg-gray-100 text-gray-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50"
              onClick={increaseCount}
              disabled={count >= (product.countInStock || 10)}
            >
              <AiOutlinePlus size={18} />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={addToCart}
        disabled={isOutOfStock}
        className={`w-full py-3.5 text-white rounded-xl transition-all duration-300 text-sm font-medium flex items-center justify-center gap-2 ${
          isOutOfStock
            ? 'bg-gray-400 cursor-not-allowed opacity-60'
            : 'bg-[#9EAD8C] hover:bg-[#8A9B78] hover:scale-[1.02] shadow-md hover:shadow-lg'
        }`}
      >
        <FaShoppingBag size={18} />
        {isOutOfStock ? 'ناموجود' : 'افزودن به سبد خرید'}
      </button>
    </div>
  );
};

export default ProductHero;